import { useState } from 'react';
import type { Comment } from '../db/types';
import { avatarColor, avatarInitial, displayName } from '../widget/avatar';
import { relativeTime } from '../widget/relativeTime';

interface ReplyAuthor {
  id?: string;
  email?: string | null;
}

interface PinProps {
  comment: Comment & {
    author?: { id: string; email: string } | null;
    replies?: Array<{ id: string; body: string; authorEmail: string; createdAt: number; author?: ReplyAuthor | null }>;
  };
  left: number;
  top: number;
  focused: boolean;
  open: boolean;
  onClick: () => void;
}

export function Pin({ comment, left, top, focused, open, onClick }: PinProps) {
  const [hover, setHover] = useState(false);

  const authorEmail = comment.author?.email ?? comment.authorEmail;
  const author = avatarColor(authorEmail);
  const replies = comment.replies ?? [];
  const replyCount = replies.length;
  const resolved = comment.status === 'resolved';

  return (
    <div
      className={
        'uin-pin-wrap' +
        (resolved ? ' resolved' : '') +
        (focused ? ' focused' : '') +
        (open ? ' open' : '')
      }
      style={{ left, top }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
    >
      <div className="uin-pin-bubble" title={authorEmail}>
        <div
          className="uin-pin-inner"
          style={{ background: author.bg, color: author.fg }}
        >
          {avatarInitial(authorEmail)}
        </div>
        {replyCount > 0 && (
          <div className="uin-pin-replies-badge" title={`${replyCount} ${replyCount === 1 ? 'reply' : 'replies'}`}>
            {replyCount}
          </div>
        )}
      </div>

      {hover && !open && (
        <PinHoverCard
          authorEmail={authorEmail}
          authorBg={author.bg}
          body={comment.body}
          createdAt={comment.createdAt}
          replyCount={replyCount}
          resolved={resolved}
        />
      )}
    </div>
  );
}

function PinHoverCard({
  authorEmail,
  authorBg,
  body,
  createdAt,
  replyCount,
  resolved,
}: {
  authorEmail: string;
  authorBg: string;
  body: string;
  createdAt: number;
  replyCount: number;
  resolved: boolean;
}) {
  return (
    <div className={'uin-pin-hovercard' + (resolved ? ' resolved' : '')}>
      <header>
        <span className="uin-pin-hovercard-avatar" style={{ background: authorBg }}>
          {avatarInitial(authorEmail)}
        </span>
        <span className="uin-pin-hovercard-name">{displayName(authorEmail)}</span>
        <span className="uin-pin-hovercard-time">{relativeTime(createdAt)}</span>
      </header>
      <div className="uin-pin-hovercard-body">{body}</div>
      {replyCount > 0 && (
        <div className="uin-pin-hovercard-replies">
          {replyCount === 1 ? '1 reply' : `${replyCount} replies`}
        </div>
      )}
    </div>
  );
}
